import { getApiErrorMessage } from '../../../services/api';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Trash2, Search, X, Eye, EyeOff, RefreshCw, Settings, Users, Edit } from 'lucide-react';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
function apiWithToken(token) { const i = axios.create({ baseURL: BACKEND }); i.interceptors.request.use(c => { c.headers.Authorization = 'Bearer ' + token; return c; }); return i; }

const MODULE_LABELS = {
  requisitions:'Purchase Requisitions', orders:'Purchase Orders', rfq:'RFQ / RFI Sourcing',
  contracts:'Contract Management', goods_receiving:'Receiving & AP', assets:'Asset Management',
  inventory:'Inventory Control', expenses:'Employee Expenses', supplier_portal:'Supplier Portal', sales_orders:'Sales Order Processing',
};
const ALL_MODULE_KEYS = Object.keys(MODULE_LABELS);

export default function NewTenantModal({ token, onClose, onCreated }) {
  const { t } = useTranslation();
  const api = apiWithToken(token);
  const [form, setForm] = useState({
    slug:'', company_name:'', company_address:'', company_city:'',
    company_country:'United States', phone:'', billing_email:'', admin_email:'',
    admin_password:'', plan:'professional', user_license_limit:50, notes:'',
    auth_method: 'email_password',
    seed_test_data: false
  });
  const [modules, setModules] = useState(['requisitions','orders','rfq','contracts','goods_receiving']);
  const [saving, setSaving] = useState(false);
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = async () => {
    if (!form.slug || !form.company_name || !form.admin_email) {
      toast.error('Slug, company name and admin email are required'); return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/eyvo-admin/tenants', { ...form, modules });
      toast.success(`Client '${form.slug}' created`);
      onCreated(); onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Creation failed')); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create New Client</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1"><Label>Company Slug * <span className="text-xs text-muted-foreground">(URL code)</span></Label><Input value={form.slug} onChange={e=>f('slug',e.target.value.toLowerCase().replace(/\s/g,'-'))} placeholder="acme-corp" /></div>
            <div className="space-y-1"><Label>Client Name *</Label><Input value={form.company_name} onChange={e=>f('company_name',e.target.value)} /></div>
            <div className="col-span-2 space-y-1"><Label>Address</Label><Input value={form.company_address} onChange={e=>f('company_address',e.target.value)} /></div>
            <div className="space-y-1"><Label>City</Label><Input value={form.company_city} onChange={e=>f('company_city',e.target.value)} /></div>
            <div className="space-y-1"><Label>Country</Label><Input value={form.company_country} onChange={e=>f('company_country',e.target.value)} /></div>
            <div className="space-y-1"><Label>Phone</Label><Input value={form.phone} onChange={e=>f('phone',e.target.value)} placeholder="+1 555 000 0000" /></div>
            <div className="space-y-1"><Label>Billing Email</Label><Input type="email" value={form.billing_email} onChange={e=>f('billing_email',e.target.value)} /></div>
            <div className="space-y-1"><Label>Admin Email *</Label><Input type="email" value={form.admin_email} onChange={e=>f('admin_email',e.target.value)} /></div>
            <div className="space-y-1"><Label>Initial Admin Password</Label><Input type="password" value={form.admin_password} onChange={e=>f('admin_password',e.target.value)} /></div>
            <div className="space-y-1"><Label>Plan</Label>
              <select value={form.plan} onChange={e=>f('plan',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                <option value="starter">Starter</option><option value="professional">Professional</option><option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="space-y-1"><Label>{t('term.userLicenceLimit')}</Label><Input type="number" value={form.user_license_limit} onChange={e=>f('user_license_limit',parseInt(e.target.value)||10)} /></div>
            <div className="col-span-2 space-y-1">
              <Label>Authentication Method</Label>
              <select value={form.auth_method} onChange={e=>f('auth_method',e.target.value)} className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                <option value="email_password">Email & Password (Default)</option>
                <option value="google_oauth">Google Workspace / OAuth</option>
                <option value="microsoft_entra">Microsoft Entra ID (Azure AD)</option>
                <option value="okta_saml">Okta SAML</option>
                <option value="onelogin">OneLogin SAML</option>
                <option value="auth0">Auth0</option>
                <option value="ping_identity">Ping Identity</option>
                <option value="ldap">LDAP / Active Directory</option>
                <option value="jumpcloud">JumpCloud</option>
                <option value="custom_saml">Custom SAML Provider</option>
              </select>
              <p className="text-xs text-muted-foreground">Can be configured later by tenant admin in Authentication Settings</p>
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Modules</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {ALL_MODULE_KEYS.map(id=>(
                <label key={id} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-xs ${
                  modules.includes(id) ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                }`}>
                  <input type="checkbox" checked={modules.includes(id)} onChange={()=>setModules(prev=>prev.includes(id)?prev.filter(m=>m!==id):[...prev,id])} className="h-3.5 w-3.5" />
                  {MODULE_LABELS[id]}
                </label>
              ))}
            </div>
          </div>
          
          <div className="border rounded-xl p-4 bg-primary/5 space-y-2">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="admin_seed_test_data"
                checked={form.seed_test_data}
                onChange={e => f('seed_test_data', e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input cursor-pointer"
                data-testid="admin-seed-test-data-checkbox"
              />
              <div>
                <label htmlFor="admin_seed_test_data" className="text-sm font-medium cursor-pointer">
                  Seed database with test data from eyvo-demo
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Copy all data from eyvo-demo (suppliers, items, requisitions, etc.) to this new tenant. 
                  Leave unchecked for an empty database.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create Client'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
